import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Inscription,
  Preloader,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { ALL_OPTION } from '@/common/constants';
import Permissions from '@/common/permissions';
import { OptionKey } from '@/common/types';
import { CLAIMS_ESCALATION_REQUIREMENTS } from '@/modules/claims/ClaimsProcessor/ClaimEscalation/store';
import { useHasAccess } from '@/modules/core/hooks';
import { useFilters } from '@/utils';

import { CLAIM_APPEAL_REQUIREMENTS, PageMode } from './ClaimsFilterSection';
import ClaimsList, {
  ACTIVE_PAGE_MODE_SEARCH_PARAM,
  ESCALATED_DEFAULT_FILTERS,
  EscalatedSharedFilters,
  SearchKeysForPageMode,
} from './ClaimsList';

export const ESCALATED_SUB_MODE_SEARCH_PARAM = 'escalatedSubMode';

enum EscalatedSubMode {
  ALL = 'ALL',
  CLAIMS = 'CLAIMS',
  APPEALS = 'APPEALS',
}

interface ButtonProps {
  $isActive?: boolean;
}

const StyledButton = styled(Button)<ButtonProps>`
  padding: ${({ theme }) => `${theme.spacings.spacing10} ${theme.spacings.spacing16}`};
  border-radius: ${({ theme }) => theme.rounds.button1Round};
  background: ${({ $isActive, theme }) => $isActive ? theme.colors.accentContainer : theme.colors.canvas};
  border: ${({ $isActive, theme }) => $isActive ? 'none' : `1px solid ${theme.colors.border1}`};
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: normal;
    color: ${({ $isActive, theme }) => $isActive ? theme.colors.textAccent : theme.colors.textBody};
  }
`;

const TabPanel = styled(Box)<{ $visible: boolean }>`
  ${({ $visible }) => !$visible && 'display: none;'}
`;

interface Props {
  hasAccessToAssignClaim?: boolean;
  isClockIn?: boolean;
  hasAccessToClockIn?: boolean;
  setTotal: (val: number | undefined) => void;
  pageMode: PageMode;
  disabled?: boolean;
}

const EscalatedClaimsList: React.FC<Props> = (props) => {
  const { setTotal, disabled } = props;
  const hasAccessToAppeals = useHasAccess(CLAIM_APPEAL_REQUIREMENTS);
  const hasAccessToEscalated = useHasAccess(CLAIMS_ESCALATION_REQUIREMENTS);
  const hasViewClaim = useHasAccess([{ permission: Permissions.VIEW_CLAIM }]);
  const showSubTabs = hasAccessToAppeals && (hasAccessToEscalated || hasViewClaim);

  const [totalAll, setTotalAll] = useState<number | undefined>();
  const [totalClaims, setTotalClaims] = useState<number | undefined>();
  const [totalAppeals, setTotalAppeals] = useState<number | undefined>();

  const history = useHistory();
  const location = useLocation();
  const [activeModeWritten, setActiveModeWritten] = useState(false);
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; });

  const searchKey = `${SearchKeysForPageMode.search}${PageMode.ESCALATED}` as const;
  const paymentTypesKey = `${SearchKeysForPageMode.paymentTypes}${PageMode.ESCALATED}` as const;
  const statusesKey = `${SearchKeysForPageMode.statuses}${PageMode.ESCALATED}` as const;
  const dateRangeKey = `${SearchKeysForPageMode.dateRange}${PageMode.ESCALATED}` as const;

  const initialValues = useMemo(() => ({
    [searchKey]: '' as string,
    [paymentTypesKey]: ALL_OPTION.value as OptionKey,
    [statusesKey]: ESCALATED_DEFAULT_FILTERS.statuses as OptionKey[],
    [dateRangeKey]: [] as Array<Date | null>,
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: '' as string,
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtersOptions = useMemo(
    () => ({ autoClear: false as const, disabled: disabled || !showSubTabs }),
    [disabled, showSubTabs]
  );

  const { filtersValues, setFilterValue } = useFilters<{
    searchESCALATED: string;
    paymentTypesESCALATED: OptionKey;
    statusesESCALATED: OptionKey[];
    dateRangeESCALATED: Array<Date | null>;
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: string;
  }>(initialValues as {
    searchESCALATED: string;
    paymentTypesESCALATED: OptionKey;
    statusesESCALATED: OptionKey[];
    dateRangeESCALATED: Array<Date | null>;
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: string;
  }, filtersOptions);

  const [subMode, setSubMode] = useState<EscalatedSubMode>(() => {
    const params = new URLSearchParams(location.search);
    const stored = params.get(ESCALATED_SUB_MODE_SEARCH_PARAM);
    if (stored === EscalatedSubMode.CLAIMS || stored === EscalatedSubMode.APPEALS) return stored;
    return EscalatedSubMode.ALL;
  });

  const sharedFilters: EscalatedSharedFilters = useMemo(() => ({
    searchString: filtersValues[searchKey] as string,
    paymentType: filtersValues[paymentTypesKey] as OptionKey,
    statuses: filtersValues[statusesKey] as OptionKey[],
    dateRange: filtersValues[dateRangeKey] as Array<Date | null>,
  }), [filtersValues, searchKey, paymentTypesKey, statusesKey, dateRangeKey]);

  const handleSharedFiltersChange = useCallback((newFilters: EscalatedSharedFilters) => {
    setFilterValue[searchKey](newFilters.searchString);
    setFilterValue[paymentTypesKey](newFilters.paymentType);
    setFilterValue[statusesKey](newFilters.statuses);
    setFilterValue[dateRangeKey](newFilters.dateRange);
  }, [setFilterValue, searchKey, paymentTypesKey, statusesKey, dateRangeKey]);

  useEffect(() => {
    if (disabled) {
      setSubMode(EscalatedSubMode.ALL);
      const params = new URLSearchParams(locationRef.current.search);
      if (params.has(ESCALATED_SUB_MODE_SEARCH_PARAM)) {
        params.delete(ESCALATED_SUB_MODE_SEARCH_PARAM);
        history.replace({ pathname: locationRef.current.pathname, search: params.toString() ? `?${params.toString()}` : '' });
      }
      setActiveModeWritten(false);
    } else if (!activeModeWritten) {
      setActiveModeWritten(true);
      const params = new URLSearchParams(locationRef.current.search);
      const hasVisitedPageMode = params.get(ACTIVE_PAGE_MODE_SEARCH_PARAM) === PageMode.ESCALATED;
      if (!params.has(statusesKey) && hasVisitedPageMode) {
        setFilterValue[statusesKey]([]);
      }
      setFilterValue[ACTIVE_PAGE_MODE_SEARCH_PARAM](PageMode.ESCALATED);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [disabled, activeModeWritten]);

  const handleSubModeChange = useCallback(
    (mode: EscalatedSubMode) => {
      setSubMode(mode);
      const params = new URLSearchParams(locationRef.current.search);
      if (mode === EscalatedSubMode.ALL) {
        params.delete(ESCALATED_SUB_MODE_SEARCH_PARAM);
      } else {
        params.set(ESCALATED_SUB_MODE_SEARCH_PARAM, mode);
      }
      history.replace({ pathname: locationRef.current.pathname, search: params.toString() ? `?${params.toString()}` : '' });
    },
    [history]
  );

  if (!showSubTabs) {
    return (
      <ClaimsList
        {...props}
        isShowOnlyEscalated
        {...(hasAccessToAppeals && !hasViewClaim ? { isAppealed: true } : {})}
        setTotal={setTotal}
      />
    );
  }

  const queryEnabled = !disabled;

  const subTabsNode = (
    <Box direction="row" align="center" gap="spacing8">
      <Box>
        <StyledButton
          data-testId="escalated-claims_sub-tab_all"
          label={typeof totalAll === 'undefined' ? (
            <Box direction="row" align="center">
              <Inscription weight={subMode === EscalatedSubMode.ALL ? 'bold' : 'normal'}>All</Inscription>
              <Box margin={{ left: 'spacing8' }}><Preloader color="iconOnColor" size={12} thinSize={2} /></Box>
            </Box>
          ) : (
            <Inscription weight={subMode === EscalatedSubMode.ALL ? 'bold' : 'normal'}>
              {`All (${totalAll})`}
            </Inscription>
          )}
          type="button"
          borderless
          $isActive={subMode === EscalatedSubMode.ALL}
          onClick={() => handleSubModeChange(EscalatedSubMode.ALL)}
        />
      </Box>

      <Box>
        <StyledButton
          data-testId="escalated-claims_sub-tab_claims"
          label={typeof totalClaims === 'undefined' ? (
            <Box direction="row" align="center">
              <Inscription weight={subMode === EscalatedSubMode.CLAIMS ? 'bold' : 'normal'}>Claims</Inscription>
              <Box margin={{ left: 'spacing8' }}><Preloader color="iconOnColor" size={12} thinSize={2} /></Box>
            </Box>
          ) : (
            <Inscription weight={subMode === EscalatedSubMode.CLAIMS ? 'bold' : 'normal'}>
              {`Claims (${totalClaims})`}
            </Inscription>
          )}
          type="button"
          borderless
          $isActive={subMode === EscalatedSubMode.CLAIMS}
          onClick={() => handleSubModeChange(EscalatedSubMode.CLAIMS)}
        />
      </Box>

      <Box>
        <StyledButton
          data-testId="escalated-claims_sub-tab_appeals"
          label={typeof totalAppeals === 'undefined' ? (
            <Box direction="row" align="center">
              <Inscription weight={subMode === EscalatedSubMode.APPEALS ? 'bold' : 'normal'}>Appeals</Inscription>
              <Box margin={{ left: 'spacing8' }}><Preloader color="iconOnColor" size={12} thinSize={2} /></Box>
            </Box>
          ) : (
            <Inscription weight={subMode === EscalatedSubMode.APPEALS ? 'bold' : 'normal'}>
              {`Appeals (${totalAppeals})`}
            </Inscription>
          )}
          type="button"
          borderless
          $isActive={subMode === EscalatedSubMode.APPEALS}
          onClick={() => handleSubModeChange(EscalatedSubMode.APPEALS)}
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <TabPanel $visible={subMode === EscalatedSubMode.ALL}>
        <ClaimsList
          {...props}
          key={String(disabled)}
          isShowOnlyEscalated
          subTabsNode={subMode === EscalatedSubMode.ALL ? subTabsNode : undefined}
          sharedFilters={sharedFilters}
          onSharedFiltersChange={handleSharedFiltersChange}
          setTotal={(val) => {
            setTotalAll(val);
            if (val !== undefined) setTotal(val);
          }}
          disabled={disabled || subMode !== EscalatedSubMode.ALL}
        />
      </TabPanel>

      <TabPanel $visible={subMode === EscalatedSubMode.CLAIMS}>
        <ClaimsList
          {...props}
          isShowOnlyEscalated
          isAppealed={false}
          subTabsNode={subMode === EscalatedSubMode.CLAIMS ? subTabsNode : undefined}
          sharedFilters={sharedFilters}
          onSharedFiltersChange={handleSharedFiltersChange}
          queryEnabled={queryEnabled}
          setTotal={setTotalClaims}
          disabled={disabled || subMode !== EscalatedSubMode.CLAIMS}
        />
      </TabPanel>

      <TabPanel $visible={subMode === EscalatedSubMode.APPEALS}>
        <ClaimsList
          {...props}
          isShowOnlyEscalated
          isAppealed
          subTabsNode={subMode === EscalatedSubMode.APPEALS ? subTabsNode : undefined}
          sharedFilters={sharedFilters}
          onSharedFiltersChange={handleSharedFiltersChange}
          queryEnabled={queryEnabled}
          setTotal={setTotalAppeals}
          disabled={disabled || subMode !== EscalatedSubMode.APPEALS}
        />
      </TabPanel>
    </Box>
  );
};

export default EscalatedClaimsList;
